import { useMemo } from 'react';

import { useSportContext } from '#src/contexts/sport/index.ts';
import { useLoadAllSubscriptions } from '#src/hooks/subscriptions/useLoadAllSubscriptions.ts';
import { joinFiltersWithOr } from '#src/lib/algolia.ts';
import { isType } from '#src/lib/gql/index.ts';

type IncludeOnlyFilter = 'Card' | 'Player';

type Props = {
  onlyType?: IncludeOnlyFilter;
  skip?: boolean;
};

export const useFavoriteFilterValue = ({ onlyType, skip }: Props) => {
  const { sport } = useSportContext();
  const { favoriteCardsBySport, favoritePlayersBySport } =
    useLoadAllSubscriptions({ skip });

  const cardsForSport = favoriteCardsBySport?.[sport!];
  const playersForSport = favoritePlayersBySport?.[sport!];

  const showCards = !onlyType || onlyType === 'Card';
  const showPlayers = !onlyType || onlyType === 'Player';

  const favoriteFilterValue = useMemo(() => {
    // by construction it can only be both are undefined or none are
    if (!cardsForSport || !playersForSport) {
      return 'player.slug:user-has-no-favorite';
    }

    const filters = [
      ...(showCards
        ? cardsForSport
            .map(c => c.anySubscribable)
            .map(
              s =>
                (isType(s, 'Card') ||
                  isType(s, 'NBACard') ||
                  isType(s, 'BaseballCard')) &&
                `slug:${s.slug}`
            )
            .filter(Boolean)
        : []),
      ...(showPlayers
        ? playersForSport
            .map(c => c.anySubscribable)
            .map(
              s =>
                (isType(s, 'Player') ||
                  isType(s, 'BaseballPlayer') ||
                  isType(s, 'NBAPlayer')) &&
                `player.slug:${s.slug}`
            )
            .filter(Boolean)
        : []),
    ];

    return joinFiltersWithOr(filters);
  }, [cardsForSport, playersForSport, showCards, showPlayers]);

  return favoriteFilterValue;
};
